import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Snackbar,
  Alert,
  LinearProgress,
  Chip,
  TextField,
  InputAdornment,
  Checkbox,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  Apps as AppsIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import DataTable from "components/common/tables/DataTable";
import { getToken } from "services/auth.service";
import { usePageTitle } from "context/PageTitleContext";
import { checkPermission } from "services/permission.service";
import { PERMISSIONS } from "auth/permissionCodes";
import "./UserPermissionManagement.css";
import { getResourceDisplayName, getModuleDisplayName, getActionLabel, getActionColor, getRoleColor } from "modules/systems/utils/permission.utils";
import { AppTextField } from "components/common/inputs";
import { AppButton } from "components/common/buttons";

const UserPermissionManagement = () => {
  const { setPageTitle } = usePageTitle();
  const [userRoles, setUserRoles] = useState([]); // Vai trò của user đang chọn
  const [allUserRoles, setAllUserRoles] = useState([]); // Vai trò của tất cả user
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [searchUser, setSearchUser] = useState("");
  const [searchPermission, setSearchPermission] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Tính toán dữ liệu đã filter
  const filteredUsers = useMemo(() => {
    return users.filter(
      (u) =>
        u.userName?.toLowerCase().includes(searchUser.toLowerCase()) ||
        u.fullName?.toLowerCase().includes(searchUser.toLowerCase())
    );
  }, [users, searchUser]);

  const applicationGroups = useMemo(() => {
    const map = new Map();

    permissions.forEach((permission) => {
      const applicationId =
        permission.applicationId ||
        permission.ApplicationId ||
        permission.moduleCode ||
        permission.ModuleCode ||
        "unassigned";

      if (!map.has(applicationId)) {
        map.set(applicationId, {
          applicationId,
          applicationName:
            permission.applicationName ||
            permission.ApplicationName ||
            getModuleDisplayName(permission.moduleCode || permission.ModuleCode) ||
            "Chưa gán ứng dụng",
          applicationCode: permission.applicationCode || permission.ApplicationCode || "",
          permissions: [],
        });
      }

      map.get(applicationId).permissions.push(permission);
    });

    return Array.from(map.values());
  }, [permissions]);

  useEffect(() => {
    if (!selectedApplicationId && applicationGroups.length > 0) {
      setSelectedApplicationId(applicationGroups[0].applicationId);
    }
  }, [applicationGroups, selectedApplicationId]);

  const filteredPermissions = useMemo(() => {
    const searchValue = searchPermission.toLowerCase();

    return permissions.filter((p) => {
      const applicationId =
        p.applicationId ||
        p.ApplicationId ||
        p.moduleCode ||
        p.ModuleCode ||
        "unassigned";
      const matchesApplication =
        selectedApplicationId === null ||
        String(applicationId) === String(selectedApplicationId);
      const matchesSearch =
        !searchValue ||
        (p.permissionCode || p.PermissionCode || p.permissionName)
          ?.toLowerCase()
          .includes(searchValue) ||
        (p.permissionDisplayName || p.PermissionDisplayName || p.description)
          ?.toLowerCase()
          .includes(searchValue) ||
        p.resourceCode?.toLowerCase().includes(searchValue) ||
        p.actionCode?.toLowerCase().includes(searchValue) ||
        p.applicationName?.toLowerCase().includes(searchValue) ||
        p.applicationCode?.toLowerCase().includes(searchValue);

      return matchesApplication && matchesSearch;
    });
  }, [permissions, searchPermission, selectedApplicationId]);

  // Set page title
  useEffect(() => {
    setPageTitle("Phân quyền");
  }, [setPageTitle]);

  useEffect(() => {
    loadData();
    loadAllUserRoles();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Lấy toàn bộ vai trò của tất cả user
  const loadAllUserRoles = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/UserRole?pageSize=1000&pageNumber=1`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setAllUserRoles(data.data || []);
      } else {
        setAllUserRoles([]);
      }
    } catch (error) {
      setAllUserRoles([]);
    }
  };

  useEffect(() => {
    if (selectedUser) {
      loadUserPermissions(selectedUser.userId);
      loadUserRoles(selectedUser.userId);
    }
  }, [selectedUser]);

  // Lấy vai trò của user
  const loadUserRoles = async (userId) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/UserRole/user/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        const roles = data.data || [];
        // Với mỗi vai trò, gọi API lấy quyền của vai trò đó
        const rolesWithPermissions = await Promise.all(
          roles.map(async (role) => {
            try {
              const permRes = await fetch(
                `${process.env.REACT_APP_API_URL}/api/RolePermission/role/${role.roleId}?pageSize=1000&pageNumber=1`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                }
              );
              if (permRes.ok) {
                const permData = await permRes.json();
                // Lưu danh sách permissionIds vào role
                return {
                  ...role,
                  permissionIds:
                    permData.permissionIds ||
                    (permData.data
                      ? permData.data.map((p) => p.permissionId)
                      : []),
                };
              } else {
                return { ...role, permissionIds: [] };
              }
            } catch (err) {
              return { ...role, permissionIds: [] };
            }
          })
        );
        setUserRoles(rolesWithPermissions);
      } else {
        setUserRoles([]);
      }
    } catch (error) {
      setUserRoles([]);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadUsers(), loadPermissions()]);
    } catch (error) {
      showSnackbar("Lỗi khi tải dữ liệu", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/User?pageSize=1000&pageNumber=1`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setUsers(data.data || []);
      }
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };

  const loadPermissions = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/SystemPermissions/all`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setPermissions(
          (data.data || []).filter(
            (permission) =>
              (permission.permissionType || permission.PermissionType) !== "Application"
          )
        );
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
    }
  };

  const loadUserPermissions = async (userId) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/UserPermission/user/${userId}?pageSize=1000&pageNumber=1`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setUserPermissions(data.data || []);
      }
    } catch (error) {
      console.error("Error loading user permissions:", error);
    }
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const isPermissionChecked = (permissionId) => {
    return userPermissions.some((up) => up.permissionId === permissionId);
  };

  const isPermissionInheritedFromRole = (permissionId) => {
    return (
      isPermissionInAdminApplication(permissionId) ||
      userRoles.some((role) => role.permissionIds?.includes(permissionId))
    );
  };

  const getPermissionApplicationId = (permission) =>
    permission?.applicationId ?? permission?.ApplicationId ?? null;

  const getRoleApplicationId = (role) =>
    role?.applicationId ?? role?.ApplicationId ?? null;

  const isAdminRole = (role) =>
    String(role?.roleName ?? role?.RoleName ?? "").toLowerCase() === "admin" ||
    String(role?.roleCode ?? role?.RoleCode ?? "").toLowerCase() === "system.admin";

  const isPermissionInAdminApplication = (permissionId) => {
    const selectedPermission = permissions.find(
      (permission) =>
        Number(permission.permissionId ?? permission.PermissionId) === Number(permissionId)
    );
    const permissionApplicationId = getPermissionApplicationId(selectedPermission);

    if (!permissionApplicationId) return false;

    return userRoles.some(
      (role) =>
        isAdminRole(role) &&
        Number(getRoleApplicationId(role)) === Number(permissionApplicationId)
    );
  };

  const handlePermissionToggle = async (permissionId, checked) => {
    if (!selectedUser) return;

    // Kiểm tra quyền động
    const result = await checkPermission(PERMISSIONS.SYSTEM.PERMISSION_FUNCTION_ASSIGN);
    if (!result.hasPermission) {
      showSnackbar("Bạn không có quyền gán quyền cho người dùng.", "warning");
      return;
    }

    // Kiểm tra vai trò
    const hasRole = userRoles && userRoles.length > 0;
    // Nếu là Admin thì không cho thao tác
    if (isPermissionInAdminApplication(permissionId)) {
      showSnackbar(
        "Người dùng đang là admin của ứng dụng này, không cần gán quyền trực tiếp.",
        "warning"
      );
      return;
    }
    // Nếu có vai trò, kiểm tra quyền đã có trong vai trò chưa
    if (hasRole && checked) {
      // Lấy tất cả quyền từ các vai trò của user (dựa vào permissionName)
      let allRolePermissionNames = [];
      for (const ur of userRoles) {
        if (ur.permissionNames && Array.isArray(ur.permissionNames)) {
          allRolePermissionNames.push(...ur.permissionNames);
        }
        // Nếu API trả về permissionIds, cần map sang permissionName
        if (ur.permissionIds && Array.isArray(ur.permissionIds)) {
          // Map permissionIds sang permissionName từ danh sách permissions
          const names = ur.permissionIds
            .map((pid) => {
              const found = permissions.find((p) => p.permissionId === pid);
              return found
                ? found.permissionCode || found.PermissionCode || found.permissionName
                : null;
            })
            .filter(Boolean);
          allRolePermissionNames.push(...names);
        }
      }
      // Lấy permissionName của quyền đang chọn
      const selectedPermission = permissions.find(
        (p) => p.permissionId === permissionId
      );
      // Log dữ liệu debug

      if (
        selectedPermission &&
        allRolePermissionNames.includes(
          selectedPermission.permissionCode ||
          selectedPermission.PermissionCode ||
          selectedPermission.permissionName
        )
      ) {

        setSnackbar({
          open: true,
          message: "Quyền này đã có theo vai trò, không cần gán trực tiếp.",
          severity: "warning",
        });
        return;
      }
    }
    const token = getToken();
    if (checked) {
      // Gán quyền cho user
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/UserPermission`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              userId: selectedUser.userId,
              permissionId,
            }),
          }
        );
        if (response.ok) {
          loadUserPermissions(selectedUser.userId);
        }
      } catch (error) {
        // Không hiển thị snackbar
      }
    } else {
      // Xóa quyền khỏi user
      try {
        // Tìm UserPermissionId
        const up = userPermissions.find(
          (up) => up.permissionId === permissionId
        );
        if (!up) return;
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/UserPermission/${up.userPermissionId}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.ok) {
          loadUserPermissions(selectedUser.userId);
        }
      } catch (error) {
        // Không hiển thị snackbar
      }
    }
  };

  // Table columns
  const userColumns = [
    { accessorKey: "userId", header: "ID", size: 60 },
    {
      accessorKey: "userName", header: "Tên đăng nhập", size: 120,
      muiTableHeadCellProps: {
        sx: {
          left: 0,
          minWidth: 50,
          maxWidth: 80,

          backgroundColor: "#0b5cab",
          color: "#fff",
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1.3,
          py: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "normal",
          wordBreak: "break-word",
          borderBottom: 0,
          borderRight: "1px solid rgba(255,255,255,.25)",
          position: "sticky",
          top: 0,
          zIndex: 3,
          "&:last-of-type": { borderRight: 0 },
          "& .MuiIconButton-root": { color: "#fff" },
          textAlign: "center",
          "& .Mui-TableHeadCell-Content": {
            justifyContent: "center",
          },
        },
      },
      muiTableBodyCellProps: {
        sx: {
          position: "sticky",
          left: 0,
          zIndex: 1,
          background: "#fff",
          minWidth: 50,
          maxWidth: 80,
          fontSize: 12,
        },
      },
    },
    {
      accessorKey: "fullName", header: "Họ tên", size: 140,
      muiTableBodyCellProps: () => {
        return {
          align: 'left',
        };
      },
    },
    {
      id: "roles",
      header: "Vai trò",
      accessorFn: (row) => {
        // Lấy danh sách vai trò của user từ allUserRoles
        const roles = allUserRoles.filter((ur) => ur.userId === row.userId);
        return roles.map((ur) => ur.roleName).join(", ");
      },
      Cell: ({ row }) => {
        const roles = allUserRoles.filter(
          (ur) => ur.userId === row.original.userId
        );
        // Áp dụng đúng mã màu custom cho từng vai trò như PermissionOverview.js
        // const getRoleColor = (roleName) => {
        //   if (!roleName) return undefined;
        //   if (roleName === "Admin") return "#9c27b0"; // tím
        //   if (roleName === "Operator") return "#2196f3"; // xanh dương
        //   if (roleName === "Creator") return "#4caf50"; // xanh lá
        //   if (roleName === "Viewer") return "#798e01ff"; // nâu/xám
        //   return undefined;
        // };
        return (
          // <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            {roles.length > 0 ? (
              roles.map((ur, idx) => (
                <Chip
                  key={idx}
                  label={ur.roleName}
                  size="small"
                  sx={{
                    backgroundColor: getRoleColor(ur.roleName),
                    color: "#fff",
                    fontWeight: 600,
                  }}
                />
              ))
            ) : (
              <Chip label="Không có" size="small" color="default" />
            )}
          </Box>
        );
      },
      size: 80,
    },
    {
      id: "status",
      header: "Trạng thái",
      size: 80,
      accessorFn: (row) => !!row.isActive,
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue() ? "Đã kích hoạt" : "Chưa kích hoạt"}
          size="small"
          sx={{
            backgroundColor: cell.getValue() ? "#4caf50" : "#ff9800",
            color: "#fff",
          }}
        />
      ),
    },
  ];

  // const getResourceDisplayName = (resource) => {
  //   const displayNames = {
  //     contract: "Hợp đồng",
  //     contract_group: "Nhóm hợp đồng",
  //     contract_history: "Lịch sử hợp đồng",
  //     contract_item: "Hạng mục hợp đồng",
  //     contract_payment: "Thanh toán hợp đồng",
  //     client: "Khách hàng",
  //     contractor: "Đơn vị thi công",
  //     contracthistory: "Lịch sử hợp đồng",
  //     contractitem: "Hạng mục hợp đồng",
  //     contractpayment: "Thanh toán hợp đồng",
  //     calendar: "Lịch biểu",
  //     personnel: "Nhân sự",
  //     department: "Phòng ban",
  //     listitemdocumenttype: "Loại tài liệu",
  //     listitempositionindepartment: "Chức vụ trong phòng ban",
  //     listitemroleincontract: "Vai trò trong hợp đồng",
  //     rolepermission: "Phân quyền",
  //     userloginhistory: "Lịch sử đăng nhập",
  //     user: "Tài khoản người dùng",
  //     subcontractdashboard: "Tổng quan quyết định giao khoán",
  //     subcontract: "Quyết định giao khoán",
  //     subcontractacceptance: "Nghiệm thu quyết định giao khoán",
  //     subcontractapproval: "Phê duyệt quyết định giao khoán",
  //     subcontractpayment: "Thanh toán quyết định giao khoán",
  //     subcontractstatistical: "Thống kê quyết định giao khoán",
  //     equipment: "Thiết bị",
  //     document_type: "Danh mục tài liệu",
  //     department_unit: "Đội trong phòng ban",
  //     release_log: "Nhật ký phát hành",
  //     permission_function: "Phân quyền chức năng",
  //     permission_application: "Phân quyền ứng dụng",
  //     subcontract_group: "Loại giao khoán",
  //     subcontract_execution_group: "Nhóm thực hiện giao khoán",
  //     subcontract_execution_group_user: "Nhân sự nhóm giao khoán",
  //     subcontract_item: "Dự toán",
  //     subcontract_history: "Lịch sử giao khoán",
  //     subcontract_approval: "Phê duyệt giao khoán",
  //     subcontract_acceptance: "Nghiệm thu giao khoán",
  //     subcontract_payment: "Thanh toán giao khoán",
  //     acceptance_approval: "Phê duyệt nghiệm thu",
  //     draft_item: "Hạng mục dự toán nháp",
  //     dashboard: "Tổng quan",
  //     statistics: "Thống kê",
  //     notification: "Thông báo",
  //     other: "Khác",
  //   };

  //   return displayNames[resource] || resource;
  // };

  // function getModuleDisplayName(moduleCode) {
  //   const displayNames = {
  //     system: "Quản lý hệ thống",
  //     contract: "Quản lý hợp đồng",
  //     subcontract: "Quản lý giao khoán",
  //     equipment: "Quản lý thiết bị",
  //   };

  //   return displayNames[moduleCode] || moduleCode;
  // }

  // const getActionLabel = (action) => {
  //   const labels = {
  //     read: "Xem",
  //     create: "Tạo",
  //     update: "Cập nhật",
  //     delete: "Xóa",
  //     submit: "Gửi duyệt",
  //     approve: "Phê duyệt",
  //     import: "Import",
  //     export: "Export",
  //     assign: "Gán quyền",
  //   };

  //   return labels[action] || action;
  // };

  // const getActionColor = (action) => {
  //   const colors = {
  //     read: "primary",
  //     create: "success",
  //     update: "warning",
  //     delete: "error",
  //     submit: "info",
  //     approve: "secondary",
  //   };

  //   return colors[action] || "default";
  // };

  // const getActionColor = (action) => {
  //   const colors = {
  //     read: "primary",
  //     create: "success",
  //     update: "warning",
  //     delete: "error",
  //     import: "info",
  //     export: "secondary",
  //     submit: "info",
  //     approve: "secondary",
  //     assign: "warning",
  //     access: "primary",
  //   };
  //   return colors[action] || "default";
  // };

  const permissionSections = useMemo(() => {
    const sections = {};

    filteredPermissions.forEach((permission) => {
      const resource =
        permission.resourceCode ||
        permission.ResourceCode ||
        permission.permissionCode?.split(".")[1] ||
        permission.permissionName?.split(".")[1] ||
        "other";
      const action =
        permission.actionCode ||
        permission.ActionCode ||
        permission.permissionCode?.split(".")[2] ||
        permission.permissionName?.split(".")[2] ||
        permission.permissionName;

      if (!sections[resource]) {
        sections[resource] = [];
      }

      sections[resource].push({
        ...permission,
        action,
      });
    });

    return Object.entries(sections).map(([resource, sectionPermissions]) => ({
      resource,
      title: getResourceDisplayName(resource),
      permissions: sectionPermissions.sort((left, right) =>
        String(left.permissionDisplayName || left.description || left.permissionName || "").localeCompare(
          String(right.permissionDisplayName || right.description || right.permissionName || ""),
          "vi",
          { sensitivity: "base" }
        )
      ),
    }));
  }, [filteredPermissions]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedApplication = applicationGroups.find(
    (application) =>
      String(application.applicationId) === String(selectedApplicationId)
  );

  const getApplicationCheckedCount = (application) => {
    return application.permissions.filter((permission) =>
      isPermissionChecked(permission.permissionId)
    ).length;
  };

  return (
    <Box className="user-permission-management tab-content">
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Grid container spacing={3}>
        {/* Left Panel - Users List */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <PersonIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Danh sách người dùng
                </Typography>
              </Box>
              <Tooltip title="Tải lại dữ liệu">
                <AppButton color="inherit" onClick={loadData}><RefreshIcon /></AppButton>
              </Tooltip>
            </Box>
            {/* <TextField
              fullWidth
              size="small"
              placeholder="Tìm kiếm người dùng..."
              value={searchUser}
              onChange={(e) => {
                setSearchUser(e.target.value);
              }}
              sx={{ mb: 2 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            /> */}
            <AppTextField
              fullWidth
              placeholder="Tìm kiếm người dùng..."
              value={searchUser}
              onChange={(value) => { setSearchUser(value); }}
              sx={{ mb: 2 }}
              startIcon={<SearchIcon fontSize="small" />}
            />
            <DataTable
              columns={userColumns}
              data={filteredUsers}
              enablePagination={false}
              getRowId={(row) => row.userId}
              muiTableBodyRowProps={({ row }) => ({
                onClick: () => {
                  setSelectedUser(row.original);
                },
                sx:
                  selectedUser && row.original.userId === selectedUser.userId
                    ? {
                      backgroundColor: "#e3f2fd",
                      borderLeft: "4px solid #1976d2",
                      cursor: "pointer",
                    }
                    : { cursor: "pointer" },
              })}
              // initialState={{
              //   density: "compact",
              //   pagination: { pageIndex: 0, pageSize: 10 },
              // }}
              initialState={{
                columnVisibility: { userId: false },
                columnPinning: { left: ["stt", "userName"] },
              }}
            />
          </Paper>
        </Grid>
        {/* Right Panel - Permissions List */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <SecurityIcon sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {selectedUser
                  ? `Quyền của: ${selectedUser.fullName}`
                  : "Chọn người dùng để phân quyền"}
              </Typography>
            </Box>
            {/* Nếu là Admin thì hiển thị thông tin toàn quyền */}
            {/* {selectedUser && selectedUser.roleName === "Admin" && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <SecurityIcon sx={{ fontSize: 64, color: "#4caf50", mb: 2 }} />
                <Typography
                  variant="h5"
                  color="primary"
                  sx={{ mb: 2, fontWeight: "bold" }}
                >
                  Quyền Admin - Toàn quyền hệ thống
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Tài khoản này có toàn quyền truy cập và quản lý mọi tính năng
                  trong hệ thống.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Không cần cấu hình quyền chi tiết cho tài khoản này.
                </Typography>
              </Box>
            )} */}
            {/* Nếu không phải Admin thì hiển thị bảng quyền */}
            {/* {selectedUser && selectedUser.roleName !== "Admin" && ( */}
            {selectedUser && (
              <>
                {/* <TextField
                  fullWidth
                  size="small"
                  placeholder="Tìm kiếm quyền..."
                  value={searchPermission}
                  onChange={(e) => {
                    setSearchPermission(e.target.value);
                  }}
                  sx={{ mb: 2 }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                /> */}
                <AppTextField
                  fullWidth
                  placeholder="Tìm kiếm quyền ..."
                  value={searchPermission}
                  onChange={(value) => { setSearchPermission(value); }}
                  sx={{ mb: 2 }}
                  startIcon={<SearchIcon fontSize="small" />}
                />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Box className="permission-application-panel">
                      <Box className="permission-application-title">
                        <AppsIcon fontSize="small" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Ứng dụng
                        </Typography>
                      </Box>
                      <Box className="permission-application-list">
                        {applicationGroups.map((application) => {
                          const isSelected =
                            String(application.applicationId) ===
                            String(selectedApplicationId);
                          const checkedCount =
                            getApplicationCheckedCount(application);

                          return (
                            <button
                              key={application.applicationId}
                              type="button"
                              className={`permission-application-item ${isSelected ? "selected" : ""
                                }`}
                              onClick={() =>
                                setSelectedApplicationId(
                                  application.applicationId
                                )
                              }
                            >
                              <span className="permission-application-name">
                                {application.applicationName}
                              </span>
                              <span className="permission-application-meta">
                                {checkedCount}/{application.permissions.length} quyền
                              </span>
                            </button>
                          );
                        })}
                      </Box>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Box className="permission-detail-panel">
                      <Box className="permission-detail-header">
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {selectedApplication?.applicationName ||
                              "Chọn ứng dụng"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {filteredPermissions.length} quyền phù hợp
                          </Typography>
                        </Box>
                      </Box>

                      <Box className="permission-section-list">
                        {permissionSections.length === 0 ? (
                          <Alert severity="info">
                            Không có quyền phù hợp trong ứng dụng đang chọn.
                          </Alert>
                        ) : (
                          permissionSections.map((section) => {
                            const checkedCount = section.permissions.filter(
                              (permission) =>
                                isPermissionChecked(permission.permissionId)
                            ).length;

                            return (
                              <Box
                                key={section.resource}
                                className="permission-section"
                              >
                                <Box className="permission-section-header">
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {section.title}
                                  </Typography>
                                  <Chip
                                    label={`${checkedCount}/${section.permissions.length}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Box>

                                <Box className="permission-item-list">
                                  {section.permissions.map((permission) => {
                                    const inherited =
                                      isPermissionInheritedFromRole(
                                        permission.permissionId
                                      );
                                    const lockedByAdminApplication =
                                      isPermissionInAdminApplication(
                                        permission.permissionId
                                      );

                                    return (
                                      <Box
                                        key={permission.permissionId}
                                        className="permission-item"
                                      >
                                        <Checkbox
                                          checked={isPermissionChecked(
                                            permission.permissionId
                                          )}
                                          onChange={(e) =>
                                            handlePermissionToggle(
                                              permission.permissionId,
                                              e.target.checked
                                            )
                                          }
                                          disabled={lockedByAdminApplication}
                                          size="small"
                                        />
                                        <Box className="permission-item-main">
                                          <Box className="permission-item-title">
                                            <Chip
                                              label={getActionLabel(
                                                permission.action
                                              )}
                                              size="small"
                                              color={getActionColor(
                                                permission.action
                                              )}
                                              variant="outlined"
                                            />
                                            {inherited && (
                                              <Tooltip title="Quyền này đã được cấp qua vai trò">
                                                <Chip
                                                  label="Theo vai trò"
                                                  size="small"
                                                  color="primary"
                                                />
                                              </Tooltip>
                                            )}
                                          </Box>
                                          <Typography variant="body2">
                                            {permission.description ||
                                              permission.permissionDisplayName ||
                                              permission.permissionName}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {permission.permissionCode || permission.permissionName}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            );
                          })
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        //   sx={
        //     snackbar.severity === "warning"
        //       ? {
        //           backgroundColor: "#ffe082 !important",
        //           color: "#212121",
        //           border: "1px solid #ffd600",
        //           fontWeight: 600,
        //         }
        //       : { fontWeight: 600 }
        //   }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserPermissionManagement;
